import React, { useContext, FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController, { SubmodelAPIPaths } from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DialogToolbarMenuItemRestoreDeleted from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemRestoreDeleted';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import DialogInfoMeta from '@/ui/Dialog/DialogInfoMeta';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { DCFieldValue } from '@/@types/lib/dataController';

const AdvancedGrobljaDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const [isGrobljeFound, setIsGroubljeFound] = useState(false);

  const { dc, mode, form, onClose, title, level, columns = 2, initialRecord } = props;

  const [submodelAPIPath, setSubmodelAPIPath] = useState<SubmodelAPIPaths | undefined>(
    initialRecord
      ? {
          grobno_mjesto_id: initialRecord.groblje_id
            ? `groblja/${initialRecord.groblje_id}/grobna-mjesta?view=picker`
            : `groblja/grobna-mjesta?view=picker`,

          registar_umrlih_id: initialRecord.registar_umrlih_id
            ? initialRecord.groblje_id
              ? `groblja/${initialRecord.groblje_id}/registar-umrlih?view=picker_neukopani&include_id=${initialRecord.registar_umrlih_id}`
              : `groblja/registar-umrlih?view=picker_neukopani&include_id=${initialRecord.registar_umrlih_id}`
            : initialRecord.groblje_id
              ? `groblja/${initialRecord.groblje_id}/registar-umrlih?view=picker_neukopani`
              : `groblja/registar-umrlih?view=picker_neukopani`,
        }
      : {
          grobno_mjesto_id: `groblja/grobna-mjesta?view=picker`,
          registar_umrlih_id: `groblja/registar-umrlih?view=picker_neukopani`,
        }
  );
  const { record, validation, fields, pickerDataControllers, onFieldChange, doInsert, doUpdate, doClose, doDelete, doRestore } =
    useFormController({ ...(props as FormControllerProps), alternativeSubmodelAPIPath: submodelAPIPath });

  useEffect(() => {
    // find the groblje from the given grobno mjesto via inital record
    // very hacky
    // what u gonna do about it?
    if (mode === 'insert' && pickerDataControllers?.grobno_mjesto_id?.records?.length && record?.grobno_mjesto_id && !isGrobljeFound) {
      const grobnoMjesto = pickerDataControllers.grobno_mjesto_id.records.find((gm) => gm.value === record.grobno_mjesto_id);
      if (grobnoMjesto && grobnoMjesto.data?.groblje_id) {
        onFieldChange(grobnoMjesto.data.groblje_id as DCFieldValue, 'groblje_id');
        setSubmodelAPIPath({
          grobno_mjesto_id: `groblja/${grobnoMjesto.data.groblje_id}/grobna-mjesta?view=picker`,
          registar_umrlih_id: `groblja/${grobnoMjesto.data.groblje_id}/registar-umrlih?view=picker_neukopani`
        });
        setIsGroubljeFound(true);
      }
    }
  }, [pickerDataControllers, record]);

  const handleFieldChange = (value: any, source: string) => {
    if (source === 'groblje_id') {
      if (value && value !== null && value.value && value.value !== null) {
        const grobljeId = value.value;
        if (onFieldChange) {
          onFieldChange(null, 'grobno_mjesto_id');
          onFieldChange(null, 'registar_umrlih_id');
          onFieldChange(value, source);
        }

        setSubmodelAPIPath({
          grobno_mjesto_id: `groblja/${grobljeId}/grobna-mjesta?view=picker`,
          registar_umrlih_id: `groblja/${grobljeId}/registar-umrlih?view=picker_neukopani`,
        });
      } else {
        setSubmodelAPIPath({
          grobno_mjesto_id: `groblja/0/grobna-mjesta?view=picker`,
          registar_umrlih_id: `groblja/0/registar-umrlih?view=picker_neukopani`,
        });

        if (onFieldChange) {
          onFieldChange(null, 'grobno_mjesto_id');
          onFieldChange(null, 'registar_umrlih_id');
          onFieldChange(null, 'groblje_id');
        }
      }
    } else if (onFieldChange) onFieldChange(value, source);
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.addRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = t('error.addRecord');
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch(() => {
          const msg = t('error.addRecord');
          showNotification(msg, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.updateRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = `${t('error.updateRecord')}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch(() => {
          const msg = `${t('error.updateRecord')}`;
          showNotification(msg, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((resolve) => {
          if (resolve.success) {
            // close
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.error(e));
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog(level);
  };

  const handleDelete = () => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.deleteRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.deleteRecord');
            showNotification(msg, 'error');
          }
        })
        .catch(() => {
          const msg = t('error.deleteRecord');
          showNotification(msg, 'error');
        });
    }
  };

  const handleRestore = () => {
    if (doRestore) {
      doRestore()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.restoreRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.restore');
            showNotification(msg, 'error');
          }
        })
        .catch(() => {
          const msg = t('error.restore');
          showNotification(msg, 'error');
        });
    }
  };

  let cmode = mode;
  if (record.is_deleted) {
    cmode = 'view';
  }
  return (
    <DynamicDialog open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t(title)}</DialogToolbarHeading>
          <ToolbarFillContent />
          {mode === 'update' && (
            <DialogToolbarMenuButton>
              {record.is_deleted ? (
                <DialogToolbarMenuItemRestoreDeleted onClick={handleRestore} />
              ) : (
                <DialogToolbarMenuItemDelete onClick={handleDelete} />
              )}
            </DialogToolbarMenuButton>
          )}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames(form || mode)}
            record={record}
            validation={validation}
            fields={fields}
            layout={dc.getForm(form || mode)?.layout}
            onFieldChange={handleFieldChange}
            mode={cmode}
            columns={columns}
            pickerDataControllers={pickerDataControllers}
            geometryChangeType="Polygon"
            mapId={46}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          <DialogInfoMeta record={record} />
        </Box>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' && record.is_deleted !== true ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default AdvancedGrobljaDialog;
